import type { EChartsOption } from 'echarts';

import { graphConfig } from '@/configs/Graphs';
import { BehaviorChart } from '@/features/forecasts/components/order-behaviour/BehaviorChart';
import { useOrderData } from '@/features/forecasts/hooks/useOrderData';
import { buildTooltipSeriesLine, joinTooltipLines } from '@/features/forecasts/utils/forecastTooltip';
import { formatIsoDateMd } from '@/utils/date';
import { format } from '@/utils/format';
import { createNoDataOption } from '@/utils/graphs';

type Props = {
  chartGroup?: string;
};

export function OrderBehaviorDow({ chartGroup }: Props) {
  const { records, total } = useOrderData();

  const { axisLabel, colors, grid } = graphConfig;
  const weekdaysFull = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const orderCount = format('Last {{total}} orders', { total });
  const dayCounts = records.reduce(
    (counts, record) => {
      const dayOfWeek = record.day_of_week;
      if (dayOfWeek == null || dayOfWeek < 1 || dayOfWeek > 7) return counts;
      counts[dayOfWeek - 1] += 1;
      return counts;
    },
    [0, 0, 0, 0, 0, 0, 0],
  );
  const mostFrequentDayIndex = dayCounts.reduce(
    (bestIndex, count, index, counts) => (count > counts[bestIndex] ? index : bestIndex),
    0,
  );
  const mostFrequentDayLabel = dayCounts[mostFrequentDayIndex] > 0 ? weekdaysFull[mostFrequentDayIndex] : 'N/A';
  const subtitle = format('Most frequently requested day of week: {{day}} • {{orderCount}}', {
    day: mostFrequentDayLabel,
    orderCount,
  });
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  const chartOptions = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      /* c8 ignore start */
      formatter: (params) => {
        const p = Array.isArray(params) ? params[0] : params;
        const data = p.data as { value: [number, string]; timestamp?: string; quantity?: number };
        const dateLabel = formatIsoDateMd(data.timestamp ?? '');
        return joinTooltipLines([
          dateLabel && buildTooltipSeriesLine({ label: 'Date', value: dateLabel }),
          data.quantity != null && buildTooltipSeriesLine({ label: 'Quantity', value: data.quantity }),
        ]);
      },
      /* c8 ignore stop */
    },
    grid,
    xAxis: {
      type: 'category',
      data: records.map((d) => `${d.order_sequence ?? ''}`),
      boundaryGap: true,
      axisLabel,
    },
    yAxis: { type: 'category', data: days, axisLabel },
    dataZoom: [{ type: 'slider', filterMode: 'none', bottom: 5, height: 20 }],
    series: [
      {
        type: 'scatter',
        data: records.map((d) => ({
          value: [d.order_sequence ?? 0, days[(d.day_of_week ?? 1) - 1] ?? ''],
          timestamp: d.date,
          quantity: d.quantity,
        })),
        itemStyle: { color: colors.actuals },
        symbolSize: 10,
      },
    ],
  } satisfies EChartsOption;

  return (
    <BehaviorChart
      chartGroup={chartGroup}
      option={total ? chartOptions : createNoDataOption('No data available')}
      title="Day of Week"
      titleInfo={subtitle}
    />
  );
}
